import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import Api, { ApiError } from "../api/api"
import { UserProfile } from "../api/models"
import { AppDispatch } from "../redux/store"
import { transferEntitlementThunk } from "../redux/libraryThunk"
import Avatar from "./widgets/Avatar"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// The Licenses page's "Transfer" workflow: name the recipient by their NFA
// account ID, check it's the right person (the public profile — name and
// avatar — comes back for a visual check), then an armed second click hands
// the entitlement over. There is no undo on this side: the recipient would
// have to transfer it back. The server decides eligibility; this dialog only
// opens for entries it marked transferable.
export default function TransferEntitlementDialog({ entitlementId, skuName, skuVersion, ilokBound, accessToken, onClose }: {
  entitlementId: string,
  skuName: string,
  skuVersion: string,
  // a live iLok deposit exists — the transfer revokes it, say so
  ilokBound: boolean,
  accessToken: string,
  onClose: () => void,
}) {
  const dispatch = useDispatch<AppDispatch>()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"recipient" | "looking-up" | "confirm" | "saving" | "success">("recipient")
  const [toUserId, setToUserId] = useState("")
  const [recipient, setRecipient] = useState<UserProfile | null>(null)
  const [armed, setArmed] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => { dialogRef.current?.showModal() }, [])

  const busy = step === "looking-up" || step === "saving"
  const trimmedId = toUserId.trim()

  const lookUp = async () => {
    setStep("looking-up")
    setErrorMessage("")
    try {
      const { user } = await Api.getUserProfile({ userId: trimmedId, accessToken })
      setRecipient(user)
      setArmed(false)
      setStep("confirm")
    } catch (err) {
      setErrorMessage(err instanceof ApiError && err.status === 404
        ? "No account with this ID. The recipient must have signed in to the User Area at least once."
        : err instanceof Error ? err.message : "unknown error")
      setStep("recipient")
    }
  }

  const transfer = async () => {
    setStep("saving")
    setErrorMessage("")
    try {
      await dispatch(transferEntitlementThunk({ entitlementId, toUserId: trimmedId, accessToken })).unwrap()
      setStep("success")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setArmed(false)
      setStep("confirm")
    }
  }

  const recipientName = recipient
    ? [recipient.firstname, recipient.lastname].filter(Boolean).join(" ") || recipient.id
    : ""

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy}>&times;</button>
        <h2 className="redeem-dialog-title">Transfer License</h2>

        {step === "success" ? (
          <>
            <p className="generate-codes-success">
              <strong>{skuName}</strong> now belongs to {recipientName}. It has left your account and your devices
              have been deactivated.
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        ) : step === "recipient" || step === "looking-up" ? (
          <>
            <p className="generate-codes-hint">
              This hands your <strong>{skuName}</strong> v{skuVersion} license to another Newfangled Audio account.
              Enter the recipient's account ID — they can find it on their Account page.
            </p>
            <form
              className="generate-codes-form"
              onSubmit={(e) => { e.preventDefault(); if (trimmedId.length > 0 && !busy) lookUp() }}
            >
              <label className="generate-codes-field">
                <span className="generate-codes-label">Recipient's account ID</span>
                <input
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={toUserId}
                  onChange={(e) => setToUserId(e.target.value)}
                  className="generate-codes-input"
                  disabled={busy}
                  autoFocus
                />
              </label>
            </form>
            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" onClick={lookUp} disabled={trimmedId.length === 0 || busy}>
                {busy ? <><Spinner size={12} /> Looking up…</> : "Next"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="generate-codes-hint">You are about to transfer <strong>{skuName}</strong> v{skuVersion} to:</p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, margin: "4px 0 12px" }}>
              <Avatar imageUrl={recipient?.imageUrl} size={40} />
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>{recipientName}</span>
                <span className="library-muted" style={{ fontSize: "9pt" }}>Account ID: {recipient?.id}</span>
              </div>
            </div>
            <ul className="generate-codes-hint" style={{ margin: "0 0 8px", paddingLeft: 18, textAlign: "left" }}>
              <li>The license and its products leave your account; any devices you activated with it are deactivated.</li>
              {ilokBound && <li>The iLok license deposited from it is revoked — the recipient can request their own.</li>}
              <li><strong>This cannot be undone.</strong> Only the recipient could transfer it back to you.</li>
            </ul>
            {armed && !busy && (
              <p style={{ margin: "0 0 8px", color: "var(--nfa-yellow)", fontSize: "10pt" }}>
                Are you sure? Click "Confirm transfer" to make it final.
              </p>
            )}
            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
            <div className="redeem-actions">
              <button onClick={() => { setArmed(false); setErrorMessage(""); setStep("recipient") }} disabled={busy}>Back</button>
              <button
                className={`btn-upgrade${armed && !busy ? " btn-armed" : ""}`}
                disabled={busy}
                onClick={() => armed ? transfer() : setArmed(true)}
              >
                {busy ? <><Spinner size={12} /> Transferring…</> : armed ? "Confirm transfer" : "Transfer"}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
